/** 1 mm ≈ 3.7795 px at 96 DPI */
const MM_TO_PX = 3.7795;

/**
 * Parse a size string (e.g. "137x49", "137 x 49", "50x80mm") into mm dimensions.
 * Falls back to 137×49 mm if unparseable.
 */
export const parseSizeMm = (size?: string): { widthMm: number; heightMm: number } => {
    if (!size) return { widthMm: 137, heightMm: 49 };
    const cleaned = size.replace(/mm/gi, '').trim();
    const match = cleaned.match(/^([\d.]+)\s*[x×]\s*([\d.]+)$/i);
    if (match) {
        return { widthMm: parseFloat(match[1]), heightMm: parseFloat(match[2]) };
    }
    return { widthMm: 137, heightMm: 49 };
};

/** Convert mm to px at 96 DPI */
export const mmToPx = (mm: number): number => Math.round(mm * MM_TO_PX);

/**
 * Scale mm dimensions to fit within a max pixel box while preserving aspect ratio.
 * Used for the in-dialog preview so the label fits regardless of actual size.
 */
export const getPreviewDimensions = (
    widthMm: number,
    heightMm: number,
    maxWidth = 300,
    maxHeight = 260,
): { width: number; height: number } => {
    if (widthMm <= 0 || heightMm <= 0) return { width: maxWidth, height: Math.round(maxWidth / (137 / 49)) };
    const aspect = widthMm / heightMm;
    let w = maxWidth;
    let h = w / aspect;
    if (h > maxHeight) {
        h = maxHeight;
        w = h * aspect;
    }
    return { width: Math.round(w), height: Math.round(h) };
};

/** Determine if the orientation is horizontal (two-column layout) */
export const isHorizontal = (orientation?: string): boolean =>
    !orientation || orientation.toLowerCase().includes('horizontal');

/**
 * Parse size AND apply orientation: for vertical, swap width/height so
 * the label is taller than wide (portrait).
 */
export const parseSizeMmOriented = (size?: string, orientation?: string): { widthMm: number; heightMm: number } => {
    const { widthMm, heightMm } = parseSizeMm(size);
    if (!isHorizontal(orientation)) {
        // Vertical: ensure width < height (portrait)
        return widthMm > heightMm
            ? { widthMm: heightMm, heightMm: widthMm }
            : { widthMm, heightMm };
    }
    return { widthMm, heightMm };
};

// ---------------------------------------------------------------------------
// Preview metrics — font sizes, QR size, and preview dimensions (in px)
// ---------------------------------------------------------------------------

export interface PreviewFontSizes {
    title: number;
    origin: number;
    footer: number;
}

export interface PreviewMetrics {
    preview: { width: number; height: number };
    qrSize: number;
    fontSize: PreviewFontSizes;
}

/**
 * Compute all preview metrics from template size, orientation, and field counts.
 * QR size and font sizes scale proportionally to the preview box dimensions.
 */
export const computePreviewMetrics = (
    size: string,
    orientation: string,
    qrFieldCount: number,
    textFieldCount: number,
    isMaterialMode = false,
): PreviewMetrics => {
    const horizontal = isHorizontal(orientation);
    const { widthMm, heightMm } = parseSizeMmOriented(size, orientation);
    const maxH = horizontal ? 260 : 360;
    const maxW = isMaterialMode ? 330 : 300;
    const preview = getPreviewDimensions(widthMm, heightMm, maxW, maxH);

    const qrSize = horizontal
        ? Math.min(
            Math.round(preview.height * (isMaterialMode ? 0.29 : 0.4)),
            Math.round(preview.width * (isMaterialMode ? 0.23 : 0.3)),
        )
        : Math.min(
            Math.round(preview.width * (isMaterialMode ? 0.36 : 0.45)),
            Math.round(preview.height * (isMaterialMode ? 0.2 : 0.25)),
        );

    const base = Math.min(preview.width, preview.height);
    const totalFields = qrFieldCount + textFieldCount;
    const densityFactor = totalFields > 6 ? 6 / totalFields : 1;

    const fontSize: PreviewFontSizes = {
        title: Math.max(18, Math.round(base * 0.08 * densityFactor)),
        origin: Math.max(6, Math.round(base * 0.065 * densityFactor)),
        footer: Math.max(5, Math.round(base * 0.055 * densityFactor)),
    };

    return { preview, qrSize, fontSize };
};
